import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  FormLabel,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Radio,
  RadioGroup,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon, AttachFile as AttachFileIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import axios from 'axios';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

// Validation schema
const POSHSchema = Yup.object().shape({
  anonymous: Yup.boolean(),
  name: Yup.string().when('anonymous', {
    is: false,
    then: () => Yup.string().required('Name is required'),
    otherwise: () => Yup.string().notRequired()
  }),
  employee_id: Yup.string(),
  department: Yup.string(),
  designation: Yup.string(),
  contact_number: Yup.string(),
  email: Yup.string().email('Invalid email'),
  comfortable_contact: Yup.string().required('Please select an option'),
  harasser_name: Yup.string().required('Required'),
  harasser_department: Yup.string(),
  harasser_designation: Yup.string(),
  direct_work_relation: Yup.string().required('Please select an option'),
  type_of_incident: Yup.string().required('Required'),
  location_of_incident: Yup.string().required('Required'),
  date_of_incident: Yup.date().required('Required'),
  time_of_incident: Yup.string().required('Required'),
  first_occurrence: Yup.string().required('Please select an option'),
  previous_incidents: Yup.string(),
  witnesses: Yup.string().required('Please select an option'),
  witnesses_details: Yup.string(),
  incident_description: Yup.string().required('Required'),
  has_supporting_evidence: Yup.string().required('Please select an option'),
  supporting_evidence: Yup.string(),
  informed_someone: Yup.string().required('Please select an option'),
  who_informed: Yup.string(),
  steps_taken_flag: Yup.string().required('Please select an option'),
  steps_taken_details: Yup.string(),
  preferred_resolution: Yup.string(),
  date_of_filing: Yup.date().required('Required'),
  reason_for_delay: Yup.string(),
  manager_informed: Yup.string().required('Please select an option'),
  declaration: Yup.boolean().oneOf([true], 'You must accept the declaration')
});

const PoshComplaintTab = () => {
  const { user, getAuthHeaders } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('form'); // 'form' or 'list'
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Initial values
  const initialValues = {
    anonymous: false,
    name: user?.full_name || '',
    employee_id: user?.employee_id || '',
    department: user?.department || '',
    designation: user?.jobTitle || '',
    contact_number: user?.contact_number || '',
    email: user?.email || '',
    comfortable_contact: '',
    harasser_name: '',
    harasser_department: '',
    harasser_designation: '',
    direct_work_relation: '',
    type_of_incident: '',
    location_of_incident: '',
    date_of_incident: '',
    time_of_incident: '',
    first_occurrence: '',
    previous_incidents: '',
    witnesses: '',
    witnesses_details: '',
    incident_description: '',
    has_supporting_evidence: '',
    supporting_evidence: '',
    informed_someone: '',
    who_informed: '',
    steps_taken_flag: '',
    steps_taken_details: '',
    preferred_resolution: '',
    date_of_filing: new Date().toISOString().split('T')[0],
    reason_for_delay: '',
    manager_informed: '',
    declaration: false
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    
    // Check total file count limit (5 files max)
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > 5) {
      toast.error(`Maximum 5 files allowed. You can select ${5 - selectedFiles.length} more file(s).`);
      return;
    }
    
    files.forEach(file => {
      // Check if file is empty (0 bytes)
      if (file.size === 0) {
        toast.error(`File ${file.name} is empty. Please select a file with content.`);
        return;
      }
      
      // Check file size (10MB limit per file)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum file size is 10MB.`);
        return;
      }

      // Check file type - comprehensive list for POSH complaints
      const allowedTypes = [
        // PDF files
        'application/pdf',
        // Images
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/tiff',
        // Documents
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Text files
        'text/plain',
        'text/csv',
        'text/html',
        // Email files - multiple MIME types for .msg files
        'message/rfc822',
        'application/vnd.ms-outlook',
        'application/x-msmsg', // Alternative MIME type for .msg files
        'application/octet-stream', // Fallback for .msg files
        // Audio files
        'audio/mpeg',
        'audio/wav',
        'audio/ogg',
        'audio/mp4',
        // Video files
        'video/mp4',
        'video/avi',
        'video/mov',
        'video/wmv',
        'video/webm',
        'video/quicktime'
      ];
      
      // Also check file extensions as fallback for MIME type issues
      const allowedExtensions = [
        '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff',
        '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
        '.txt', '.csv', '.html',
        '.msg', '.eml', // Email file extensions
        '.mp3', '.wav', '.ogg', '.m4a',
        '.mp4', '.avi', '.mov', '.wmv', '.webm', '.qt'
      ];
      
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);
      
      // Debug logging for file type issues
      if (!isValidType && !isValidExtension) {
        console.log('File validation failed:', {
          fileName: file.name,
          fileType: file.type,
          fileExtension: fileExtension,
          allowedTypes: allowedTypes,
          allowedExtensions: allowedExtensions
        });
        toast.error(`File ${file.name} is not a supported format.`);
        return;
      }
      
      validFiles.push(file);
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Download supporting document
  const downloadDocument = async (complaintId, fileName, originalName) => {
    try {
      const response = await axios.get(
        `${API_URL}/posh/complaint/${complaintId}/document/${fileName}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('Document not found');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  // Handle submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!user?.id) {
      toast.error('User information not available.');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      
      // Append form data
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined) {
          if (key === 'date_of_incident' || key === 'date_of_filing') {
            formData.append(key, new Date(values[key]).toISOString());
          } else {
            formData.append(key, values[key]);
          }
        }
      });
      
      // Append files
      selectedFiles.forEach(file => {
        formData.append('supporting_documents', file);
      });

      const response = await axios.post(
        `${API_URL}/posh/complaint`,
        formData,
        { 
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('POSH complaint submitted successfully');
      resetForm();
      setSelectedFiles([]);
      fetchComplaints();
      setViewMode('list');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  // useFormik
  const formik = useFormik({
    initialValues,
    validationSchema: POSHSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false
  });

  // Fetch complaints
  const fetchComplaints = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/posh/complaint/user/${user.id}`,
        { headers: getAuthHeaders() }
      );
      setComplaints(response.data || []);
    } catch (error) {
      console.error(error);
      // Only show error if it's not a 404 (no complaints found)
      if (error.response?.status !== 404) {
        toast.error('Failed to fetch complaints');
      } else {
        // User has no complaints, set empty array
        setComplaints([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) fetchComplaints();
  }, [user]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'success';
      case 'Pending': return 'warning';
      case 'Under Investigation': return 'info';
      case 'Escalated': return 'error';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          POSH Complaint Form 
        </Typography>
        <Box>
          <Button
            variant={viewMode === 'form' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('form')}
            sx={{ mr: 1 }}
          >
            Submit New Complaint
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
          >
            View My Complaints ({complaints.length})
          </Button>
        </Box>
      </Box>

      {viewMode === 'form' ? (
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              (Prevention of Sexual Harassment at Workplace – As per POSH Act, 2013)
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'grey.200',
              mt: 2,
              mb: 3
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                <strong>Note:</strong> This form is to be used for reporting incidents of sexual harassment at the workplace. 
                All information provided will be kept strictly confidential and shared only with the Internal Committee (IC) for the purpose of inquiry.
              </Typography>
            </Box>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* I. Complainant's Details */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                   Complainant's Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Would you prefer to fill this with your personal details or as anonymous
                </Typography>
                <RadioGroup
                  row
                  name="anonymous"
                  value={formik.values.anonymous.toString()}
                  onChange={(e) => formik.setFieldValue('anonymous', e.target.value === 'true')}
                >
                  <FormControlLabel value="false" control={<Radio />} label="Self" />
                  <FormControlLabel value="true" control={<Radio />} label="Anonymous" />
                </RadioGroup>
              </Grid>

              {!formik.values.anonymous && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="employee_id"
                      label="Employee ID"
                      value={formik.values.employee_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="department"
                      label="Department"
                      value={formik.values.department}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="designation"
                      label="Designation"
                      value={formik.values.designation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="contact_number"
                      label="Contact Number"
                      value={formik.values.contact_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Are you comfortable being contacted by the Internal Committee?
                </Typography>
                <RadioGroup
                  row
                  name="comfortable_contact"
                  value={formik.values.comfortable_contact}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.comfortable_contact && formik.errors.comfortable_contact && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.comfortable_contact}
                  </Typography>
                )}
              </Grid>

              {/* II. Details of the Alleged Harasser */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Details of the Alleged Harasser
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  (Person against whom the complaint is being made)
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="harasser_name"
                  label="Name"
                  value={formik.values.harasser_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.harasser_name && Boolean(formik.errors.harasser_name)}
                  helperText={formik.touched.harasser_name && formik.errors.harasser_name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="harasser_department"
                  label="Department"
                  value={formik.values.harasser_department}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="harasser_designation"
                  label="Designation"
                  value={formik.values.harasser_designation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Do you work directly with this person for your regular work activities?
                </Typography>
                <RadioGroup
                  row
                  name="direct_work_relation"
                  value={formik.values.direct_work_relation}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.direct_work_relation && formik.errors.direct_work_relation && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.direct_work_relation}
                  </Typography>
                )}
              </Grid>

              {/* III. Incident Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Incident Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="type_of_incident"
                  label="Type of Incident (physical/verbal/visual/online)"
                  value={formik.values.type_of_incident}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.type_of_incident && Boolean(formik.errors.type_of_incident)}
                  helperText={formik.touched.type_of_incident && formik.errors.type_of_incident}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="location_of_incident"
                  label="Location of Incident"
                  value={formik.values.location_of_incident}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.location_of_incident && Boolean(formik.errors.location_of_incident)}
                  helperText={formik.touched.location_of_incident && formik.errors.location_of_incident}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  name="date_of_incident"
                  label="Date of Incident"
                  value={formik.values.date_of_incident}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.date_of_incident && Boolean(formik.errors.date_of_incident)}
                  helperText={formik.touched.date_of_incident && formik.errors.date_of_incident}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="time"
                  name="time_of_incident"
                  label="Time of Incident"
                  value={formik.values.time_of_incident}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.time_of_incident && Boolean(formik.errors.time_of_incident)}
                  helperText={formik.touched.time_of_incident && formik.errors.time_of_incident}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Is this the first occurrence?
                </Typography>
                <RadioGroup
                  row
                  name="first_occurrence"
                  value={formik.values.first_occurrence}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.first_occurrence && formik.errors.first_occurrence && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.first_occurrence}
                  </Typography>
                )}
              </Grid>

              {formik.values.first_occurrence === 'no' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="previous_incidents"
                    label="If No, provide details of previous incidents"
                    value={formik.values.previous_incidents}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Were there any witnesses?
                </Typography>
                <RadioGroup
                  row
                  name="witnesses"
                  value={formik.values.witnesses}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.witnesses && formik.errors.witnesses && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.witnesses}
                  </Typography>
                )}
              </Grid>

              {formik.values.witnesses === 'yes' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="witnesses_details"
                    label="If yes, please provide names/contact info"
                    value={formik.values.witnesses_details}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}

              {/* IV. Incident Description */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Incident Description
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please describe the incident in detail. Mention the sequence of events, exact words or actions, and any context you feel is important. 
                  Please describe the behaviour / action(s) in detail. Include facts to the best of your knowledge.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="incident_description"
                  label="Incident Description"
                  value={formik.values.incident_description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={6}
                  error={formik.touched.incident_description && Boolean(formik.errors.incident_description)}
                  helperText={formik.touched.incident_description && formik.errors.incident_description}
                  required
                />
              </Grid>

              {/* V. Supporting Evidence */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                  Supporting Evidence (if any)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Documents, emails, text messages, screenshots, etc. attached?
                </Typography>
                <RadioGroup
                  row
                  name="has_supporting_evidence"
                  value={formik.values.has_supporting_evidence}
                  onChange={(e) => formik.setFieldValue('has_supporting_evidence', e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.has_supporting_evidence && formik.errors.has_supporting_evidence && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.has_supporting_evidence}
                  </Typography>
                )}
              </Grid>

              {formik.values.has_supporting_evidence === 'yes' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="supporting_evidence"
                      label="If yes, list or describe them briefly"
                      value={formik.values.supporting_evidence}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Upload Supporting Documents
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        border: '2px dashed',
                        borderColor: selectedFiles.length > 0 ? 'success.main' : 'grey.300',
                        backgroundColor: selectedFiles.length > 0 ? 'success.50' : 'grey.50',
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'primary.50',
                        },
                      }}
                      onClick={() => document.getElementById('file-input').click()}
                    >
                      <input
                        id="file-input"
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.eml,.msg,.mp3,.wav,.ogg,.m4a,.mp4,.avi,.mov,.wmv,.webm,.qt"
                      />
                      <CloudUploadIcon sx={{ fontSize: 48, color: selectedFiles.length > 0 ? 'success.main' : 'grey.500', mb: 2 }} />
                                             <Typography variant="h6" gutterBottom>
                         {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : 'Click to select files'}
                       </Typography>
                       <Typography variant="body2" color="text.secondary">
                         Supported formats: PDF, Images, Documents, Text files, Emails, Audio, Video (Max: 10MB each, 5 files total)
                       </Typography>
                       {selectedFiles.length > 0 && (
                         <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                           {selectedFiles.length}/5 files selected
                         </Typography>
                       )}
                    </Paper>
                  </Grid>
                  
                  {selectedFiles.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Selected Files:
                      </Typography>
                      <List dense>
                        {selectedFiles.map((file, index) => (
                          <ListItem key={index} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mb: 1 }}>
                            <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <ListItemText
                              primary={file.name}
                              secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => removeFile(index)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </>
              )}

              {/* VI. Actions Already Taken */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Actions Already Taken (if any)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Did you inform anyone about the incident?
                </Typography>
                <RadioGroup
                  row
                  name="informed_someone"
                  value={formik.values.informed_someone}
                  onChange={(e) => formik.setFieldValue('informed_someone', e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.informed_someone && formik.errors.informed_someone && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.informed_someone}
                  </Typography>
                )}
              </Grid>

              {formik.values.informed_someone === 'yes' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="who_informed"
                    label="If yes, who did you inform and when?"
                    value={formik.values.who_informed}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    multiline
                    rows={2}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Have any steps been taken (formally/informally) to resolve the matter?
                </Typography>
                <RadioGroup
                  row
                  name="steps_taken_flag"
                  value={formik.values.steps_taken_flag}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.steps_taken_flag && formik.errors.steps_taken_flag && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.steps_taken_flag}
                  </Typography>
                )}
              </Grid>

              {formik.values.steps_taken_flag === 'yes' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="steps_taken_details"
                    label="If yes, please explain"
                    value={formik.values.steps_taken_details}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    multiline
                    rows={3}
                  />
                </Grid>
              )}

              {/* VII. Preferred Outcome or Resolution */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Preferred Outcome or Resolution
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="preferred_resolution"
                  label="Do you have any preferences regarding how this should be resolved or addressed?"
                  value={formik.values.preferred_resolution}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* VIII. Reporting Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    Reporting Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  name="date_of_filing"
                  label="Date of Filing the Complaint"
                  value={formik.values.date_of_filing}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={formik.touched.date_of_filing && Boolean(formik.errors.date_of_filing)}
                  helperText={formik.touched.date_of_filing && formik.errors.date_of_filing}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="reason_for_delay"
                  label="Reason for delay in filing (if beyond 3 months)"
                  value={formik.values.reason_for_delay}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={1}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Is your Manager/HR informed about the incident?
                </Typography>
                <RadioGroup
                  row
                  name="manager_informed"
                  value={formik.values.manager_informed}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {formik.touched.manager_informed && formik.errors.manager_informed && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.manager_informed}
                  </Typography>
                )}
              </Grid>

              {/* Declaration */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="declaration"
                        checked={formik.values.declaration}
                        onChange={(e) => formik.setFieldValue('declaration', e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body1" sx={{ 
                        mt: 1, 
                        lineHeight: 1.7, 
                        flex: 1,
                        fontSize: '1.1rem',
                      }}>
                        I hereby declare that the information provided above is accurate to the best of my knowledge. 
                        I understand that filing a false complaint is a serious offence under the POSH Act. 
                        I understand that the Internal Committee will maintain confidentiality during the investigation as per the provisions of the POSH Act.
                      </Typography>
                    }
                  />
                  {formik.touched.declaration && formik.errors.declaration && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {formik.errors.declaration}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button type="button" variant="outlined" onClick={() => setViewMode('list')}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}>
                  {formik.isSubmitting ? <CircularProgress size={20} /> : 'Submit Complaint'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
            My POSH Complaints
          </Typography>

          {complaints.length === 0 ? (
            <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No complaints submitted yet.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {complaints.map((complaint) => (
                <Grid item xs={12} key={complaint._id}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          Complaint #{complaint.poshId || complaint._id.slice(-6)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={complaint.status || 'Pending'}
                            color={getStatusColor(complaint.status)}
                            size="small"
                          />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Incident Type:</strong> {complaint.type_of_incident}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Date:</strong> {formatDate(complaint.date_of_incident)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Description:</strong> {complaint.incident_description}
                      </Typography>
                      {complaint.supporting_documents && complaint.supporting_documents.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Supporting Documents ({complaint.supporting_documents.length}):
                          </Typography>
                          <List dense>
                            {complaint.supporting_documents.map((doc, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <AttachFileIcon sx={{ mr: 1, color: 'primary.main', fontSize: 16 }} />
                                <ListItemText
                                  primary={doc.originalName}
                                  secondary={`${(doc.fileSize / 1024 / 1024).toFixed(2)} MB`}
                                  sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    aria-label="download"
                                    onClick={() => downloadDocument(complaint._id, doc.fileName, doc.originalName)}
                                    color="primary"
                                    size="small"
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {/* Resolution Date */}
                      {complaint.resolution_date && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Resolution Date:
                          </Typography>
                          <Typography variant="body2" sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                            {formatDate(complaint.resolution_date)}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Taken History */}
                      {complaint.actionTakenHistory && complaint.actionTakenHistory.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '18px' }}>
                            Action Taken History
                          </Typography>
                          <Box sx={{ 
                            maxHeight: '400px', 
                            overflowY: 'auto',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 2,
                            p: 2,
                            bgcolor: 'grey.50'
                          }}>
                            {complaint.actionTakenHistory.map((action, index) => (
                            <Box key={index} sx={{ mb: 3 }}>
                              <Box sx={{
                                p: 2,
                                bgcolor: action.deletedAt ? 'grey.50' : 'info.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: action.deletedAt ? 'grey.300' : 'info.300',
                                opacity: action.deletedAt ? 0.8 : 1,
                                boxShadow: action.deletedAt ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Chip
                                      label={action.status}
                                      color={action.status === 'Resolved' ? 'success' : action.status === 'Escalated' ? 'error' : 'warning'}
                                      size="small"
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                    {/* Hide priority from employee view */}
                                    {action.deletedAt && (
                                      <Chip
                                        label="Previous Update"
                                        color="default"
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{
                                    color: 'text.secondary',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}>
                                    {formatDateTime(action.created_at)}
                                  </Typography>
                                </Box>
                                {action.internal_committee_notes && (
                                  <Typography variant="body2" sx={{
                                    mt: 1,
                                    lineHeight: 1.6,
                                    color: 'text.primary'
                                  }}>
                                    <strong>Internal Committee Notes:</strong> {action.internal_committee_notes}
                                  </Typography>
                                )}
                                {action.resolution_notes && (
                                  <Typography variant="body2" sx={{
                                    mt: 1,
                                    lineHeight: 1.6,
                                    color: 'text.primary'
                                  }}>
                                    <strong>Resolution Notes:</strong> {action.resolution_notes}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                          </Box>
                        </Grid>
                      )}
                      
                      {/* Current Admin Notes (if no history) */}
                      {(!complaint.actionTakenHistory || complaint.actionTakenHistory.length === 0) && (
                        <>
                      {complaint.internal_committee_notes && (
                            <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Internal Committee Notes:
                          </Typography>
                          <Typography variant="body2" sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                            {complaint.internal_committee_notes}
                          </Typography>
                            </Grid>
                      )}

                      {complaint.resolution_notes && (
                            <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Resolution Notes:
                          </Typography>
                          <Typography variant="body2" sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                            {complaint.resolution_notes}
                          </Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {complaint.actions_taken && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Action Taken:</strong> {complaint.actions_taken}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PoshComplaintTab;
